// frontend/src/presentation/components/SttDiagnosticsOverlay.tsx
//
// Panel diagnostyki dyktowania offline (Vosk) — „krok 7" walidacji FAZY 1.
// Renderuje się TYLKO gdy włączona flaga: `?sttdiag=1` w URL (raz widziana,
// zapamiętana w `localStorage`, przeżywa reload PWA i przejście w offline) albo
// `localStorage['sttdiag'] === '1'`. Bez flagi → `null`, zero śladu w UI dla
// zwykłego użytkownika (żadnej regresji).
//
// Po co: telefon zleceniodawcy to nieznany słaby Android; przy maszynie w hali
// nie ma laptopa ani `chrome://inspect`. Panel pokazuje na ekranie to, co
// normalnie poszłoby tylko w `console` — `inputSampleRate`, czy było zejście do
// 16 kHz, czy tor audio ożył (`sawAudioSignal`), po jakim czasie przyszedł
// pierwszy `partialresult`, ile trwała sesja, transkrypt. Pracownik robi
// screenshot i odsyła. Console (`[STT-DIAG]`) leci równolegle — to druga droga.
//
// Wpięty w Screen1 jednym elementem, obok `<OfflineModelBanner/>`; poza tym
// samowystarczalny. Czyta wspólny `sttDiagStore`, zasilany z `useDictation`.

import { useSyncExternalStore, type CSSProperties } from 'react'

import { sttDiagStore } from '../speech/sttDiagStore'

const FLAG = 'sttdiag'

/**
 * Czy panel ma się pokazać. `?sttdiag=1` w URL jednorazowo włącza i ZAPAMIĘTUJE
 * (kolejne wejścia z ekranu głównego PWA — już bez query stringa — dalej
 * pokazują panel, aż ktoś wyczyści `localStorage`). Cała detekcja w jednym
 * `try` — brak `location` / zablokowany `localStorage` (tryb prywatny,
 * miniatury) → panel po prostu się nie renderuje.
 */
function overlayEnabled(): boolean {
  try {
    if (new URLSearchParams(window.location.search).has(FLAG)) {
      try {
        window.localStorage.setItem(FLAG, '1')
      } catch {
        // nie udało się zapamiętać — trudno, w tej sesji i tak pokazujemy
      }
      return true
    }
    return window.localStorage.getItem(FLAG) === '1'
  } catch {
    return false
  }
}

export function SttDiagnosticsOverlay(): JSX.Element | null {
  const snap = useSyncExternalStore(
    sttDiagStore.subscribe,
    sttDiagStore.getSnapshot,
    sttDiagStore.getSnapshot,
  )

  if (!overlayEnabled()) return null

  const dump = [
    ...snap.lines,
    snap.transcript ? `TRANSKRYPT: ${snap.transcript}` : '',
  ]
    .filter(Boolean)
    .join('\n')

  const copy = (): void => {
    try {
      void navigator.clipboard?.writeText(dump || '(pusto)')
    } catch {
      // schowek niedostępny — i tak wszystko jest na ekranie do przepisania
    }
  }

  return (
    <div
      role="log"
      aria-label="Diagnostyka STT offline"
      style={{
        position: 'absolute',
        left: 8,
        right: 8,
        bottom: 8,
        zIndex: 90,
        maxHeight: '38%',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '8px 10px',
        borderRadius: 10,
        background: 'rgba(2,6,23,0.92)',
        color: '#e2e8f0',
        font: '11px/1.35 ui-monospace, SFMono-Regular, Menlo, monospace',
        boxShadow: '0 8px 28px rgba(0,0,0,0.55)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong style={{ letterSpacing: 0.4 }}>STT DIAG</strong>
        <span style={{ opacity: 0.6 }}>{snap.lines.length} zdarz.</span>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={copy} style={btnStyle}>
          Kopiuj
        </button>
        <button type="button" onClick={() => sttDiagStore.clear()} style={btnStyle}>
          Wyczyść
        </button>
      </div>
      <pre
        style={{
          margin: 0,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {dump || '(brak zdarzeń — dotknij mikrofonu i powiedz kilka słów)'}
      </pre>
    </div>
  )
}

const btnStyle: CSSProperties = {
  padding: '3px 8px',
  borderRadius: 6,
  border: '1px solid rgba(148,163,184,0.5)',
  background: 'transparent',
  color: 'inherit',
  font: 'inherit',
  cursor: 'pointer',
}
